import { Children, cloneElement } from "react";

import flattenChildren from "react-keyed-flatten-children";

import { useTheme } from "../../../context/theme";
import useShadow from "../../../styles/shadows/useShadow";
import isTouchDevice from "../../../utils/isTouchDevice";
import { Box } from "../../layout";
import ContextPopup from "../components/ContextPopup";
import PopupArrow from "../components/PopupArrow";
import { Position } from "../types";

type Contrast = "low" | "high";

type Props = {
  tooltip: React.ReactNode;
  children: React.ReactElement<any>;
  closeDelay?: number;
  position?: Position;
  contrast?: Contrast;
  showArrow?: boolean;
  maxWidth?: string;
  anchorWidth?: string;
  anchorHeight?: string;
  boundingElementRef?: React.MutableRefObject<HTMLElement | null>;
  enabled?: boolean;
};

export default function Tooltip({
  tooltip,
  children,
  closeDelay = 0,
  position = "top",
  contrast = "low",
  showArrow,
  maxWidth = "400px",
  anchorWidth,
  anchorHeight,
  boundingElementRef,
  enabled = true
}: Props) {
  const { backgroundColor, color, boxShadow } = usePopupStyles(contrast);

  return (
    <ContextPopup
      position={position}
      hover={isTouchDevice() ? false : true}
      closeDelay={closeDelay}
      anchorWidth={anchorWidth}
      anchorHeight={anchorHeight}
      boundingElementRef={boundingElementRef}
      enablePopup={enabled}
    >
      {{
        anchor: ({ anchorProps }) => {
          const numOfChildren = Children.toArray(flattenChildren(children))
            .length;

          if (numOfChildren === 1) {
            return cloneElement(children, {
              ...anchorProps,
              cursor: enabled && "help"
            });
          } else {
            console.error("A tooltip must have one child");
            return children;
          }
        },
        popup: () => {
          return (
            <Box display="flex" alignItems="center" position="relative">
              <Box
                backgroundColor={backgroundColor}
                color={color}
                padding="large"
                borderRadius="small"
                maxWidth={maxWidth}
                boxShadow={boxShadow}
                allChildrenStyles={{
                  color: `${color} !important`,
                  backgroundColor: `${backgroundColor} !important`
                }}
              >
                {tooltip}
              </Box>
              {showArrow && (
                <PopupArrow position={position} color={backgroundColor} />
              )}
            </Box>
          );
        }
      }}
    </ContextPopup>
  );
}

function usePopupStyles(contrast: Contrast) {
  const theme = useTheme();
  const shadow = useShadow("small");

  return contrast === "low"
    ? {
        backgroundColor: theme.foreground,
        color: theme["neutral-high-content"],
        boxShadow: shadow
      }
    : {
        backgroundColor: theme["neutral-high-surface"],
        color: theme["contentOn-neutral-high-surface"],
        boxShadow: undefined
      };
}
